import type { ReactNode, SVGProps } from "react";
import type { TravelDirection, TravelMode } from "@seagames/contracts";

/**
 * The travel glyphs, her paths (`l3.js` ICONS / `register.js`): a plane, a
 * coach, a ship, and the landing / taking-off planes above a runway that
 * mark an arrival and a departure. 24-unit boxes, stroked, sized by class.
 */
type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

function Glyph({ children, ...props }: P & { children: ReactNode }) {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false} {...props}>
			{children}
		</svg>
	);
}

export const PlaneIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M10.5 13.5 4 12l-1 1.5 6 3.5 3.5 6 1.5-1-1.5-6.5 4-4 3.4-3.4a1.6 1.6 0 0 0-2.3-2.3L14.5 9.5Z" />
	</Glyph>
);

export const CoachIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="5" y="3.5" width="14" height="14" rx="2.5" />
		<path d="M5 11h14M8 17.5V20M16 17.5V20" />
		<circle cx="8.5" cy="14.3" r=".8" />
		<circle cx="15.5" cy="14.3" r=".8" />
	</Glyph>
);

export const ShipIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4 15.5 5.5 19h13l1.5-3.5Z" />
		<path d="M6.5 15.5V10h11v5.5M12 10V4.5M9 7h6" />
	</Glyph>
);

export const ArrivalIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M3 20.5h18" />
		<path d="M4.6 10.4 3 8.8l1.8-4 1 .5c.5.25.8.75.8 1.3s.3 1.05.8 1.3l1 .5 2.7-5.4.95.48a1.8 1.8 0 0 1 1 1.37l.65 4.86a1.8 1.8 0 0 0 .98 1.37l3.96 1.98c.38.2.7.5.9.86l.54.93c.44.8-.05 1.78-.95 1.89l-1.06.14a2.2 2.2 0 0 1-1.23-.22L5.07 10.74a1.8 1.8 0 0 1-.47-.34Z" />
	</Glyph>
);

export const DepartureIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M3 20.5h18" />
		<path d="M6.7 16.2 4.6 15.8 2.8 12.2l1-.5a1.8 1.8 0 0 1 1.62 0l.15.1a1.8 1.8 0 0 0 1.62 0L8.2 11 5.5 5.6l.8-.4a1.8 1.8 0 0 1 1.88.18l3.62 2.7a1.8 1.8 0 0 0 1.89.18l3.77-1.85a2.2 2.2 0 0 1 1.56-.15l.98.27a1.26 1.26 0 0 1 .78 1.79l-.34.68c-.2.41-.54.76-.96.97L7.8 16.04a1.8 1.8 0 0 1-1.1.16Z" />
	</Glyph>
);

/** The mode's glyph: plane, coach or ship. */
export function ModeIcon({ mode, ...p }: P & { mode: TravelMode }) {
	return mode === "AIR" ? <PlaneIcon {...p} /> : mode === "SEA" ? <ShipIcon {...p} /> : <CoachIcon {...p} />;
}

/** Landing plane for an arrival, taking off for a departure. */
export function DirectionIcon({ direction, ...p }: P & { direction: TravelDirection }) {
	return direction === "ARRIVAL" ? <ArrivalIcon {...p} /> : <DepartureIcon {...p} />;
}
